import { Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { HostConfigService } from '@core/services/host-config.service';

export interface PreferenciaColorRolDto {
  nombreRol: string;
  codigoColor: string;
}

export interface GuardarPreferenciaColorDto {
  nombreRol: string;
  codigoColor: string;
  advertencia: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PreferenciaColorService {
  public readonly preferenciasActuales: Signal<PreferenciaColorRolDto[]>;
  private readonly preferencias = signal<PreferenciaColorRolDto[]>([]);

  constructor(
    private readonly http: HttpClient,
    private readonly hostConfigService: HostConfigService
  ) {
    this.preferenciasActuales = this.preferencias.asReadonly();
  }

  public cargarPreferencias(): Observable<PreferenciaColorRolDto[]> {
    const baseUrl = this.hostConfigService.getCurrentBaseServiceUrl();
    return this.http.get<PreferenciaColorRolDto[]>(`${baseUrl}Autorizacion/preferencias-color`)
      .pipe(
        tap(preferencias => this.preferencias.set(preferencias))
      );
  }

  public guardarPreferencia(nombreRol: string, codigoColor: string): Observable<GuardarPreferenciaColorDto> {
    const baseUrl = this.hostConfigService.getCurrentBaseServiceUrl();
    return this.http.put<GuardarPreferenciaColorDto>(
      `${baseUrl}Autorizacion/preferencias-color`,
      { nombreRol, codigoColor }
    ).pipe(
      tap(() => {
        const actuales = this.preferencias();
        const index = actuales.findIndex(p => p.nombreRol === nombreRol);
        if (index >= 0) {
          const copia = [...actuales];
          copia[index] = { nombreRol, codigoColor };
          this.preferencias.set(copia);
        } else {
          this.preferencias.set([...actuales, { nombreRol, codigoColor }]);
        }
      })
    );
  }

  public obtenerColorPorRol(nombreRol: string): string | null {
    const preferencia = this.preferencias().find(p => p.nombreRol === nombreRol);
    return preferencia?.codigoColor ?? null;
  }

  public actualizarPreferenciasLocal(preferencias: PreferenciaColorRolDto[]): void {
    this.preferencias.set(preferencias);
  }

  public upsertLocal(nombreRol: string, codigoColor: string): void {
    const actuales = this.preferencias();
    const index = actuales.findIndex(p => p.nombreRol === nombreRol);
    const nuevas = [...actuales];
    if (index >= 0) {
      nuevas[index] = { nombreRol, codigoColor };
    } else {
      nuevas.push({ nombreRol, codigoColor });
    }
    this.preferencias.set(nuevas);
  }

  public obtenerColoresOcupados(): Map<string, string> {
    const mapa = new Map<string, string>();
    for (const pref of this.preferencias()) {
      mapa.set(pref.codigoColor, pref.nombreRol);
    }
    return mapa;
  }
}
